import React, { useMemo, useState } from 'react';
import { PlusIcon, Trash2Icon, SaveIcon, LockIcon, ShieldIcon, PencilIcon, XIcon } from 'lucide-react';
import { getAllRecords, saveRecord, deleteRecord, ITEM_CATEGORIES, type SKRRecord, type StoredItem } from '../lib/skrStore';
const ADMIN_PASSCODE = 'steca-admin';
const emptyItem = (): StoredItem => ({
  id: Math.random().toString(36).slice(2),
  category: ITEM_CATEGORIES[0],
  description: '',
  quantity: '',
  estimatedValue: ''
});
const emptyRecord = (): SKRRecord => ({
  code: '',
  holderName: '',
  accountRef: '',
  facility: '',
  issueDate: '',
  expiryDate: '',
  status: 'Active',
  items: [emptyItem()],
  notes: ''
});
export function Admin() {
  const [authed, setAuthed] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [authError, setAuthError] = useState(false);
  const [records, setRecords] = useState<SKRRecord[]>(() => getAllRecords());
  const [draft, setDraft] = useState<SKRRecord>(emptyRecord());
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const refresh = () => setRecords(getAllRecords());
  const isEditing = editingCode !== null;
  const totalItems = useMemo(() => records.reduce((sum, r) => sum + r.items.length, 0), [records]);
  if (!authed) {
    return <div className="flex w-full items-center justify-center bg-steca-50/40 px-4 py-24" data-id="element-111" data-mp="31qiix464ym7r">
        <form onSubmit={e => {
        e.preventDefault();
        if (passcode === ADMIN_PASSCODE) {
          setAuthed(true);
          setAuthError(false);
        } else {
          setAuthError(true);
        }
      }} className="w-full max-w-sm rounded-2xl border border-steca-100 bg-white p-8 shadow-sm" data-id="element-112" data-mp="2wwnz80qj678o">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-steca-600 text-white" data-id="element-113" data-mp="2dz3actnrn8o2">
            <LockIcon className="h-6 w-6" data-id="element-114" data-mp="24axyqxro4hco" />
          </div>
          <h1 className="mt-4 text-center font-display text-xl font-bold text-steca-ink" data-id="element-115" data-mp="29akcr9fbm1mj">
            Admin Access
          </h1>
          <p className="mt-1 text-center text-sm text-steca-ink/60" data-id="element-116" data-mp="1uemlo5k97nh6">
            Enter the admin passcode to manage SKR records.
          </p>
          <input type="password" value={passcode} onChange={e => {
          setPasscode(e.target.value);
          setAuthError(false);
        }} placeholder="Passcode" className="mt-5 w-full rounded-xl border border-steca-200 px-4 py-2.5 outline-none focus:border-steca-500 focus:ring-2 focus:ring-steca-200" data-id="element-117" data-mp="1zeqt7636p7v9" />
          {authError && <p className="mt-2 text-sm text-red-600" data-id="element-118" data-mp="2ia7c7pohoxcy">Incorrect passcode.</p>}
          <button type="submit" className="mt-4 w-full rounded-xl bg-steca-600 px-5 py-3 font-semibold text-white transition-colors hover:bg-steca-700" data-id="element-119" data-mp="360unmkxm6549">
            Unlock
          </button>
          <p className="mt-4 text-center text-xs text-steca-ink/40" data-id="element-120" data-mp="zb9htamai9pq">
            Demo passcode: <code className="font-mono" data-id="element-121" data-mp="ucpxfench5zh">steca-admin</code>
          </p>
        </form>
      </div>;
  }
  const updateItem = (id: string, patch: Partial<StoredItem>) => {
    setDraft(d => ({
      ...d,
      items: d.items.map(it => it.id === id ? {
        ...it,
        ...patch
      } : it)
    }));
  };
  const startEdit = (record: SKRRecord) => {
    setDraft(JSON.parse(JSON.stringify(record)));
    setEditingCode(record.code);
    setFormError(null);
    setSavedMsg(null);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const resetForm = () => {
    setDraft(emptyRecord());
    setEditingCode(null);
    setFormError(null);
  };
  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!/^\d{7,11}$/.test(draft.code)) {
      setFormError('SKR code must be 7 to 11 digits.');
      return;
    }
    if (!draft.holderName.trim()) {
      setFormError('Holder name is required.');
      return;
    }
    if (!isEditing && getAllRecords().some(r => r.code === draft.code)) {
      setFormError('An SKR with this code already exists. Edit it below instead.');
      return;
    }
    const cleaned: SKRRecord = {
      ...draft,
      items: draft.items.filter(it => it.description.trim() || it.quantity.trim())
    };
    saveRecord(cleaned);
    refresh();
    setSavedMsg(`SKR ${cleaned.code} saved.`);
    resetForm();
  };
  const handleDelete = (code: string) => {
    deleteRecord(code);
    refresh();
    if (editingCode === code) resetForm();
  };
  const inputClass = 'w-full rounded-lg border border-steca-200 px-3 py-2 text-sm outline-none focus:border-steca-500 focus:ring-2 focus:ring-steca-200';
  return <div className="w-full bg-steca-50/40" data-id="element-122" data-mp="3trqopgkobsui">
      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6" data-id="element-123" data-mp="3tt1oz6ewuigv">
        <div className="flex items-center gap-3" data-id="element-124" data-mp="2l7bjurd7qt1">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-steca-600 text-white" data-id="element-125" data-mp="2lp7eqa4830f">
            <ShieldIcon className="h-5 w-5" data-id="element-126" data-mp="1n4sxbjukkik9" />
          </div>
          <div data-id="element-127" data-mp="1i3delyfv6o51">
            <h1 className="font-display text-2xl font-extrabold text-steca-ink" data-id="element-128" data-mp="1i3lodhgoxdc0">SKR Admin</h1>
            <p className="text-sm text-steca-ink/60" data-id="element-129" data-mp="1d63hrxq5ba76">
              {records.length} records · {totalItems} stored items
            </p>
          </div>
        </div>
        {savedMsg && <div className="mt-4 rounded-xl border border-steca-200 bg-steca-50 px-4 py-3 text-sm font-medium text-steca-700" data-id="element-130" data-mp="25sxzimvkvvgy">
            {savedMsg}
          </div>}
        {/* Form */}
        <form onSubmit={handleSave} className="mt-6 rounded-2xl border border-steca-100 bg-white p-6 shadow-sm" data-id="element-131" data-mp="1w34n5jnm3cbn">
          <div className="flex items-center justify-between" data-id="element-132" data-mp="1r30fmj4olrxk">
            <h2 className="font-display text-lg font-bold text-steca-ink" data-id="element-133" data-mp="2olildkcv10b9">
              {isEditing ? `Edit SKR ${editingCode}` : 'Add new SKR record'}
            </h2>
            {isEditing && <button type="button" onClick={resetForm} className="inline-flex items-center gap-1 text-sm font-medium text-steca-600 hover:text-steca-700" data-id="element-134" data-mp="hx9pfllhke8u">
                <XIcon className="h-4 w-4" data-id="element-135" data-mp="85vpxdtjbefp" /> Cancel edit
              </button>}
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-136" data-mp="d3nm4k17pmpp">
            <div data-id="element-137" data-mp="3vabnpzx1zrqm">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-steca-ink/60" data-id="element-138" data-mp="3v9tgy9crlv4g">
                SKR Code (7–11 digits)
              </label>
              <input value={draft.code} onChange={e => setDraft(d => ({
              ...d,
              code: e.target.value.replace(/\D/g, '').slice(0, 11)
            }))} placeholder="e.g. 48120736" disabled={isEditing} className={`${inputClass} font-mono disabled:bg-slate-50`} data-id="element-139" data-mp="3ldytn0xljbvc" />
            </div>
            <div data-id="element-140" data-mp="393r2s4sd891g">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-steca-ink/60" data-id="element-141" data-mp="3937jokqzbelu">
                Holder name
              </label>
              <input value={draft.holderName} onChange={e => setDraft(d => ({
              ...d,
              holderName: e.target.value
            }))} className={inputClass} data-id="element-142" data-mp="3it0uvkn7lrpk" />
            </div>
            <div data-id="element-143" data-mp="3rs5vcwibzv4d">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-steca-ink/60" data-id="element-144" data-mp="3rrmap81aqsmn">
                Account reference
              </label>
              <input value={draft.accountRef} onChange={e => setDraft(d => ({
              ...d,
              accountRef: e.target.value
            }))} className={inputClass} data-id="element-145" data-mp="5m8vbygi941k" />
            </div>
            <div data-id="element-146" data-mp="27e57996l4fwv">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-steca-ink/60" data-id="element-147" data-mp="27dlz2k6ql5w1">
                Facility
              </label>
              <input value={draft.facility} onChange={e => setDraft(d => ({
              ...d,
              facility: e.target.value
            }))} placeholder="e.g. Dar es Salaam Vault — Chamber 3" className={inputClass} data-id="element-148" data-mp="1glv8zuq6220r" />
            </div>
            <div data-id="element-149" data-mp="1vahp3ynmaxt1">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-steca-ink/60" data-id="element-150" data-mp="1v8y6i1ehoao3">
                Issue date
              </label>
              <input type="date" value={draft.issueDate} onChange={e => setDraft(d => ({
              ...d,
              issueDate: e.target.value
            }))} className={inputClass} data-id="element-151" data-mp="12laqkzo5y87s" />
            </div>
            <div data-id="element-152" data-mp="1bms4qci0fztu">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-steca-ink/60" data-id="element-153" data-mp="1bnbm9s3r0m7c">
                Expiry date
              </label>
              <input type="date" value={draft.expiryDate} onChange={e => setDraft(d => ({
              ...d,
              expiryDate: e.target.value
            }))} className={inputClass} data-id="element-154" data-mp="2n0e4erlhxjtm" />
            </div>
            <div data-id="element-155" data-mp="7ebidzmyb0ch">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-steca-ink/60" data-id="element-156" data-mp="2licv9jnrnzc">
                Status
              </label>
              <select value={draft.status} onChange={e => setDraft(d => ({
              ...d,
              status: e.target.value as SKRRecord['status']
            }))} className={inputClass} data-id="element-157" data-mp="3tra2m7tiwylm">
                <option data-id="element-158" data-mp="3frih7m0ogfxl">Active</option>
                <option data-id="element-159" data-mp="3fsk5is6peyx9">On Hold</option>
                <option data-id="element-160" data-mp="3asxg7kdlps83">Released</option>
              </select>
            </div>
          </div>
          {/* Items */}
          <div className="mt-6" data-id="element-161" data-mp="1e171s6gh2x2s">
            <div className="flex items-center justify-between" data-id="element-162" data-mp="1dzci0vgxtb0a">
              <h3 className="font-display text-sm font-bold uppercase tracking-wide text-steca-ink/70" data-id="element-163" data-mp="19116mgazusfy">
                Stored items
              </h3>
              <button type="button" onClick={() => setDraft(d => ({
              ...d,
              items: [...d.items, emptyItem()]
            }))} className="inline-flex items-center gap-1 rounded-lg bg-steca-50 px-3 py-1.5 text-sm font-medium text-steca-700 hover:bg-steca-100" data-id="element-164" data-mp="2golvrk2x0fhb">
                <PlusIcon className="h-4 w-4" data-id="element-165" data-mp="2kpxkzohvmyoz" /> Add item
              </button>
            </div>
            <div className="mt-3 space-y-3" data-id="element-166" data-mp="1x0d4dc974div">
              {draft.items.map(item => <div key={item.id} className="grid gap-2 rounded-xl border border-steca-100 bg-steca-50/50 p-3 sm:grid-cols-[10rem_1fr_7rem_8rem_auto]" data-id="element-167" data-mp="1t2p3ztoqhsjl">
                  <select value={item.category} onChange={e => updateItem(item.id, {
                category: e.target.value
              })} className={inputClass} data-id="element-168" data-mp="21zfyi1nn0zc2">
                    {ITEM_CATEGORIES.map(c => <option key={c} data-id="element-169" data-mp="3kzf7uw9z4tma">{c}</option>)}
                  </select>
                  <input value={item.description} onChange={e => updateItem(item.id, {
                description: e.target.value
              })} placeholder="Description" className={inputClass} data-id="element-170" data-mp="3prqh4pqltrri" />
                  <input value={item.quantity} onChange={e => updateItem(item.id, {
                quantity: e.target.value
              })} placeholder="Qty" className={inputClass} data-id="element-171" data-mp="8iab32ins99p" />
                  <input value={item.estimatedValue} onChange={e => updateItem(item.id, {
                estimatedValue: e.target.value
              })} placeholder="Est. value" className={inputClass} data-id="element-172" data-mp="3us4dvbmr9fa8" />
                  <button type="button" onClick={() => setDraft(d => ({
                ...d,
                items: d.items.filter(it => it.id !== item.id)
              }))} className="inline-flex items-center justify-center rounded-lg px-2 text-red-500 hover:bg-red-50" aria-label="Remove item" data-id="element-173" data-mp="2obiyocgv90l3">
                    <Trash2Icon className="h-4 w-4" data-id="element-174" data-mp="1lhluawiw0mqc" />
                  </button>
                </div>)}
            </div>
          </div>
          <div className="mt-5" data-id="element-175" data-mp="1gkda6qe6i8mg">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-steca-ink/60" data-id="element-176" data-mp="1cgdv8c8jrhv7">
              Notes (optional)
            </label>
            <textarea value={draft.notes} onChange={e => setDraft(d => ({
            ...d,
            notes: e.target.value
          }))} rows={2} className={inputClass} data-id="element-177" data-mp="17jf3u1g9pp0p" />
          </div>
          {formError && <p className="mt-3 text-sm text-red-600" data-id="element-178" data-mp="39febeom6dj2y">{formError}</p>}
          <button type="submit" className="mt-5 inline-flex items-center gap-2 rounded-xl bg-steca-600 px-5 py-3 font-semibold text-white transition-colors hover:bg-steca-700" data-id="element-179" data-mp="39d0f6cfre0tt">
            <SaveIcon className="h-4 w-4" data-id="element-180" data-mp="3o40yjci1nf4q" />
            {isEditing ? 'Save changes' : 'Create SKR record'}
          </button>
        </form>
        {/* Records list */}
        <div className="mt-8" data-id="element-181" data-mp="2qi3a6ygzgfkv">
          <h2 className="mb-3 font-display text-lg font-bold text-steca-ink" data-id="element-182" data-mp="2vfbup5plsvpa">Existing records</h2>
          {records.length === 0 ? <p className="rounded-xl border border-dashed border-steca-200 bg-white p-8 text-center text-sm text-steca-ink/50" data-id="element-183" data-mp="30ddclnbamoz6">
              No SKR records yet. Add one above.
            </p> : <div className="space-y-3" data-id="element-184" data-mp="fnfl9jidpzvt">
              {records.map(r => <div key={r.code} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-steca-100 bg-white p-4 shadow-sm" data-id="element-185" data-mp="jp15xr15nigk">
                  <div data-id="element-186" data-mp="dalugcflga0k">
                    <div className="flex items-center gap-2" data-id="element-187" data-mp="db3nz1avga4s">
                      <span className="font-mono text-sm font-bold text-steca-700" data-id="element-188" data-mp="s73511fylkm2">{r.code}</span>
                      <span className="rounded-full bg-steca-50 px-2 py-0.5 text-xs font-medium text-steca-700" data-id="element-189" data-mp="n76xis81zb1r">
                        {r.status}
                      </span>
                    </div>
                    <div className="mt-0.5 text-sm text-steca-ink" data-id="element-190" data-mp="w96j7xu0j3b1">{r.holderName}</div>
                    <div className="text-xs text-steca-ink/50" data-id="element-191" data-mp="1axua3qswyoun">
                      {r.items.length} item{r.items.length !== 1 ? 's' : ''} · {r.facility || '—'}
                    </div>
                  </div>
                  <div className="flex gap-2" data-id="element-192" data-mp="1jx7moscc3tjl">
                    <button onClick={() => startEdit(r)} className="inline-flex items-center gap-1 rounded-lg border border-steca-200 px-3 py-1.5 text-sm font-medium text-steca-700 hover:bg-steca-50" data-id="element-193" data-mp="1jvwiwsiw2lsh">
                      <PencilIcon className="h-4 w-4" data-id="element-194" data-mp="23ihkgo2a2gbs" /> Edit
                    </button>
                    <button onClick={() => handleDelete(r.code)} className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50" data-id="element-195" data-mp="1piycpwddp700">
                      <Trash2Icon className="h-4 w-4" data-id="element-196" data-mp="1kkn2vln32whs" /> Delete
                    </button>
                  </div>
                </div>)}
            </div>}
        </div>
      </section>
    </div>;
}